/**
 * Página de Gestión de Asistencia
 */

import { useState, useMemo } from "react";
import { Card, CardContent, Button, Badge, Select } from "@/components/ui";
import { useConfirm } from "@/components/ui/ConfirmDialog";
import {
  Calendar,
  Plus,
  Search,
  Users,
  Clock,
  Edit,
  Trash2,
  Loader2,
  CheckCircle,
  ClipboardCheck,
  BarChart3,
} from "lucide-react";
import {
  useMeetingsByClubAndPeriod,
  useDeleteMeeting,
} from "@/hooks/useAttendance";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import { useActivePeriod } from "@/hooks/useCatalogs";
import { Can } from "@/components/auth/Can";
import {
  getMeetingTypeLabel,
  getMeetingTypeColor,
} from "@/lib/services/attendance";
import { toJsDate } from "@/lib/services/activitySubmissions";
import MeetingModal from "./components/MeetingModal";
import AttendancePanel from "./components/AttendancePanel";
import type { Meeting } from "@/types";

export default function AttendancePage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [showAttendancePanel, setShowAttendancePanel] = useState(false);
  const [attendanceMeeting, setAttendanceMeeting] = useState<Meeting | null>(
    null,
  );

  // Data - usando scope del usuario
  const { profile } = useAuthStore();
  const { isClubDirector } = usePermissions();
  const { data: clubs, isLoading: loadingClubs } = useAccessibleClubs();
  const { data: activePeriod } = useActivePeriod();
  const deleteMutation = useDeleteMeeting();
  const confirm = useConfirm();

  // Usar el club del director o el primero disponible
  const defaultClub =
    isClubDirector && profile?.directorOfClub
      ? profile.directorOfClub
      : clubs?.[0];

  // Club seleccionado manualmente (admins con varios clubes); cae al default.
  const [selectedClubId, setSelectedClubId] = useState<string>("");
  const effectiveClub =
    (selectedClubId && clubs?.find((c) => c.id === selectedClubId)) ||
    defaultClub;

  const { data: meetings, isLoading: loadingMeetings } =
    useMeetingsByClubAndPeriod(effectiveClub?.id, activePeriod?.id);

  // Filtrar reuniones
  const filteredMeetings = useMemo(() => {
    if (!meetings) return [];

    if (!searchTerm) return meetings;

    return meetings.filter((m) =>
      m.title.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [meetings, searchTerm]);

  // Estadísticas
  const stats = useMemo(() => {
    if (!meetings) return { total: 0, closed: 0, open: 0, avgAttendance: 0 };

    const closed = meetings.filter((m) => m.isClosed);
    const totalAttendance = closed.reduce(
      (sum, m) => sum + (m.attendanceCount || 0),
      0,
    );
    const totalMembers = closed.reduce(
      (sum, m) => sum + (m.totalMembers || 0),
      0,
    );

    return {
      total: meetings.length,
      closed: closed.length,
      open: meetings.filter((m) => !m.isClosed).length,
      avgAttendance:
        totalMembers > 0
          ? Math.round((totalAttendance / totalMembers) * 100)
          : 0,
    };
  }, [meetings]);

  const handleEditMeeting = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setShowMeetingModal(true);
  };

  const handleTakeAttendance = (meeting: Meeting) => {
    setAttendanceMeeting(meeting);
    setShowAttendancePanel(true);
  };

  const handleDeleteMeeting = async (meeting: Meeting) => {
    const ok = await confirm({
      title: "Eliminar reunión",
      description: `¿Seguro que deseas eliminar la reunión "${meeting.title}"? Esta acción no se puede deshacer.`,
      variant: "danger",
      confirmText: "Eliminar",
    });
    if (ok) {
      await deleteMutation.mutateAsync({
        id: meeting.id,
        clubId: meeting.clubId,
      });
    }
  };

  const isLoading = loadingClubs || loadingMeetings;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            Asistencia
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px] text-sm sm:text-base">
            Gestiona reuniones y control de asistencia
          </p>
        </div>
        <Can permission="attendance.write" clubId={selectedClubId}>
          <Button
            onClick={() => {
              setSelectedMeeting(null);
              setShowMeetingModal(true);
            }}
            aria-label="Nueva Reunión"
            className="shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-300"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Nueva Reunión</span>
            <span className="sm:hidden">Nueva</span>
          </Button>
        </Can>
      </div>

      {/* Estadísticas rápidas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
          <CardContent className="py-3 px-3 sm:py-5 sm:px-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {stats.total}
                </p>
                <p className="text-xs sm:text-sm font-medium text-gray-500">
                  Reuniones Totales
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
          <CardContent className="py-3 px-3 sm:py-5 sm:px-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-yellow-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/30 group-hover:scale-110 transition-transform duration-300">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {stats.open}
                </p>
                <p className="text-xs sm:text-sm font-medium text-gray-500">
                  Pendientes
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
          <CardContent className="py-3 px-3 sm:py-5 sm:px-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {stats.closed}
                </p>
                <p className="text-xs sm:text-sm font-medium text-gray-500">
                  Cerradas
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
          <CardContent className="py-3 px-3 sm:py-5 sm:px-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {stats.avgAttendance}%
                </p>
                <p className="text-xs sm:text-sm font-medium text-gray-500">
                  Asistencia Promedio
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Búsqueda + selector de club (si hay varios) */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
            placeholder="Buscar reuniones..."
            className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300"
          />
        </div>
        {clubs && clubs.length > 1 && (
          <div className="sm:w-64">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Ver reuniones del club
            </label>
            <Select
              value={effectiveClub?.id || ""}
              onChange={(e) => setSelectedClubId(e.target.value)}
              className="border-2 border-gray-200 rounded-xl"
              aria-label="Elegir club para ver sus reuniones"
            >
              {clubs.map((club) => (
                <option key={club.id} value={club.id}>
                  {club.name}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>

      {/* Lista de reuniones */}
      {filteredMeetings.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent>
            <div className="text-center py-12 text-gray-500">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-lg font-medium">No hay reuniones</p>
              <p className="text-sm mt-2">
                {searchTerm
                  ? "No se encontraron reuniones con ese nombre"
                  : "Crea tu primera reunión para comenzar a registrar asistencia"}
              </p>
              <Can permission="attendance.write" clubId={selectedClubId}>
                <Button
                  onClick={() => setShowMeetingModal(true)}
                  className="mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Reunión
                </Button>
              </Can>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredMeetings.map((meeting) => {
            const mDate = toJsDate(meeting.date);
            return (
              <Card
                key={meeting.id}
                className="hover:shadow-lg transition-all duration-300"
              >
                <CardContent className="py-3 sm:py-4">
                  <div className="flex items-center gap-3 sm:gap-4">
                    {/* Fecha */}
                    <div className="flex-shrink-0 w-12 sm:w-16 text-center">
                      <p className="text-xl sm:text-2xl font-bold text-gray-900">
                        {mDate ? mDate.getDate() : "—"}
                      </p>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase">
                        {mDate
                          ? mDate.toLocaleDateString("es-CO", {
                              month: "short",
                            })
                          : ""}
                      </p>
                    </div>

                    {/* Separador */}
                    <div className="w-px h-10 sm:h-12 bg-gray-200" />

                    {/* Información */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-gray-900 truncate text-sm sm:text-base">
                          {meeting.title}
                        </h3>
                        <Badge
                          className={`${getMeetingTypeColor(meeting.type)} text-[10px] sm:text-xs`}
                        >
                          {getMeetingTypeLabel(meeting.type)}
                        </Badge>
                        {meeting.isClosed ? (
                          <Badge className="bg-green-100 text-green-700 text-[10px] sm:text-xs">
                            Cerrada
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-700 text-[10px] sm:text-xs">
                            Abierta
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 flex-wrap">
                        {meeting.startTime && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {meeting.startTime}
                            {meeting.endTime && ` - ${meeting.endTime}`}
                          </span>
                        )}
                        {meeting.location && (
                          <span className="truncate max-w-[120px] sm:max-w-none">
                            {meeting.location}
                          </span>
                        )}
                        {meeting.isClosed && meeting.attendanceCount > 0 && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {meeting.attendanceCount} asistentes
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => handleTakeAttendance(meeting)}
                        aria-label={
                          meeting.isClosed ? "Ver Lista" : "Pasar Lista"
                        }
                      >
                        <ClipboardCheck className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">
                          {meeting.isClosed ? "Ver Lista" : "Pasar Lista"}
                        </span>
                      </Button>

                      <button
                        onClick={() => handleEditMeeting(meeting)}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors duration-200"
                        title="Editar"
                        aria-label="Editar reunión"
                      >
                        <Edit className="w-4 h-4" />
                      </button>

                      {!meeting.isClosed && (
                        <button
                          onClick={() => handleDeleteMeeting(meeting)}
                          className="p-1.5 sm:p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors duration-200"
                          title="Eliminar"
                          aria-label="Eliminar reunión"
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal de Reunión */}
      {effectiveClub && activePeriod && (
        <MeetingModal
          isOpen={showMeetingModal}
          onClose={() => {
            setShowMeetingModal(false);
            setSelectedMeeting(null);
          }}
          clubId={effectiveClub.id}
          periodId={activePeriod.id}
          meeting={selectedMeeting}
        />
      )}

      {/* Panel de Asistencia */}
      {attendanceMeeting && (
        <AttendancePanel
          isOpen={showAttendancePanel}
          onClose={() => {
            setShowAttendancePanel(false);
            setAttendanceMeeting(null);
          }}
          meeting={attendanceMeeting}
        />
      )}
    </div>
  );
}
